import { useEffect, useState, type FormEvent } from "react";
import { createClass, deleteClass, getClassMembers, listClasses, listCourts, listSports, listTenantMembers, updateClass } from "../lib/api/endpoints";
import { useSession } from "../lib/session/SessionContext";
import type { ClassDto, ClassMemberDetailsDto, CreateClassRequest, CourtDto, SportDto, TenantMemberDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { Label } from "./Label";
import { C, fieldStyle } from "../theme";
import { ClassEnrollmentPanel } from "./ClassEnrollmentPanel";
import { TimePicker } from "./DateTimePicker";
import { ApiError } from "../lib/api/client";
import { ConfirmDialog } from "./ConfirmDialog";
import { Snackbar } from "./Snackbar";
import { Select } from "./Select";
import { ClassDetailsDialog } from "./ClassDetailsDialog";

const weekdays = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
const emptyDraft: CreateClassRequest = { name: "", courtId: "", coachUserId: "", dayOfWeek: 1, startTime: "19:00", endTime: "20:00", capacity: 20, isActive: true };
const classFieldStyle = { ...fieldStyle, width: "100%", minWidth: 0, height: 40, boxSizing: "border-box" as const };

function classErrorMessage(cause: unknown, fallback: string): string {
  if (!(cause instanceof ApiError)) return cause instanceof Error ? cause.message : fallback;
  if (cause.extensions?.code === "CLASS_OUTSIDE_OPERATING_HOURS" || cause.detail === "The class must be scheduled within operating hours.") {
    return "Não há horário de funcionamento do CT configurado para esse dia ou intervalo.";
  }
  return cause.detail ?? cause.message;
}

export function ClassManagementTab({ onChanged }: { onChanged?: () => void }) {
  const { accessToken } = useSession();
  const token = accessToken ?? "";
  const [classes, setClasses] = useState<ClassDto[]>([]);
  const [courts, setCourts] = useState<CourtDto[]>([]);
  const [sports, setSports] = useState<SportDto[]>([]);
  const [members, setMembers] = useState<TenantMemberDto[]>([]);
  const [draft, setDraft] = useState<CreateClassRequest>(emptyDraft);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingDelete, setPendingDelete] = useState<ClassDto | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<{ classId: string; items: ClassMemberDetailsDto[] } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<ClassDto | null>(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const [classPage, courtPage, sportPage, tenantMembers] = await Promise.all([
        listClasses(token, { isActive: undefined, page: 1, pageSize: 100 }),
        listCourts(token, { isActive: true, page: 1, pageSize: 100 }),
        listSports(token, { isActive: true, page: 1, pageSize: 100 }),
        listTenantMembers(token),
      ]);
      setClasses(classPage.items);
      setCourts(courtPage.items);
      setSports(sportPage.items);
      setMembers(tenantMembers);
      setDraft(current => ({ ...current, courtId: current.courtId || courtPage.items[0]?.id || "", coachUserId: current.coachUserId || tenantMembers[0]?.userId || "" }));
    } catch (cause) {
      setError(classErrorMessage(cause, "Não foi possível carregar as aulas."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      await createClass(token, { ...draft, startTime: `${draft.startTime}:00`, endTime: `${draft.endTime}:00` });
      setDraft(current => ({ ...emptyDraft, courtId: current.courtId, coachUserId: current.coachUserId }));
      await load();
      onChanged?.();
      setSuccess("Aula adicionada com sucesso.");
    } catch (cause) {
      setError(classErrorMessage(cause, "Não foi possível cadastrar a aula."));
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (item: ClassDto) => {
    if (!token) return;
    try {
      await updateClass(token, item.id, { isActive: !item.isActive });
      await load();
      onChanged?.();
    } catch (cause) {
      setError(classErrorMessage(cause, "Não foi possível alterar o status da aula."));
    }
  };

  const remove = async (item: ClassDto) => {
    if (!token) return;
    setPendingDelete(item);
  };

  const confirmRemove = async () => {
    if (!token || !pendingDelete) return;
    try {
      await deleteClass(token, pendingDelete.id);
      await load();
      onChanged?.();
      setPendingDelete(null);
      setSuccess("Aula excluída com sucesso.");
    } catch (cause) {
      setError(classErrorMessage(cause, "Não foi possível excluir a aula."));
    }
  };

  const showMembers = async (item: ClassDto) => {
    if (!token) return;
    try {
      setSelectedMembers({ classId: item.id, items: await getClassMembers(token, item.id) });
    } catch (cause) {
      setError(classErrorMessage(cause, "Não foi possível carregar os alunos da aula."));
    }
  };

  return <section aria-label="Gerenciar aulas" style={{ display: "grid", gap: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div><Label>Cadastro de aulas</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Crie a programação semanal e defina professor, quadra e limite de alunos.</div></div>
      <div style={{ display: "flex", gap: 8 }}><Button variant="outline" onClick={() => void load()} disabled={loading}>Atualizar</Button><Button onClick={() => setCreateOpen(true)}>Adicionar aula</Button></div>
    </div>
    {createOpen && <div role="dialog" aria-modal="true" aria-label="Adicionar aula" style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: C.overlay, overflowY: "auto" }}><Card style={{ width: "min(900px, 100%)", maxHeight: "calc(100vh - 32px)", overflow: "visible" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}><Label style={{ color: C.ink }}>Adicionar aula</Label><Button variant="ghost" ariaLabel="Fechar cadastro de aula" onClick={() => setCreateOpen(false)}>×</Button></div><form id="class-create-form" className="class-create-form" onSubmit={async event => { await submit(event); setCreateOpen(false); }} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, alignItems: "end", minWidth: 0 }}>
      <label style={{ display: "grid", minWidth: 0, gap: 4, color: C.light, fontSize: 11 }}>Descrição<input className="class-schedule-placeholder" required value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} style={classFieldStyle} placeholder="Ex.: Futebol iniciante" /></label>
      <label style={{ display: "grid", minWidth: 0, gap: 4, color: C.light, fontSize: 11 }}>Dia<Select ariaLabel="Dia da aula" value={String(draft.dayOfWeek)} onChange={value => setDraft({ ...draft, dayOfWeek: Number(value) })} options={weekdays.map((day, index) => ({ value: String(index), label: day }))} style={{ width: "100%" }} /></label>
      <label style={{ display: "grid", minWidth: 0, gap: 4, color: C.light, fontSize: 11 }}>Início<TimePicker ariaLabel="Início da aula" value={draft.startTime} onChange={value => setDraft({ ...draft, startTime: value })} /></label>
      <label style={{ display: "grid", minWidth: 0, gap: 4, color: C.light, fontSize: 11 }}>Fim<TimePicker ariaLabel="Fim da aula" value={draft.endTime} onChange={value => setDraft({ ...draft, endTime: value })} /></label>
      <label style={{ display: "grid", minWidth: 0, gap: 4, color: C.light, fontSize: 11 }}>Limite<input required min={1} type="number" value={draft.capacity} onChange={event => setDraft({ ...draft, capacity: Number(event.target.value) })} style={classFieldStyle} /></label>
      <label style={{ display: "grid", minWidth: 0, gridColumn: 1, gap: 4, color: C.light, fontSize: 11 }}>Quadra<Select ariaLabel="Quadra da aula" value={draft.courtId} onChange={value => setDraft({ ...draft, courtId: value })} options={courts.map(court => ({ value: court.id, label: court.name }))} placeholder="Selecione a quadra" style={{ width: "100%" }} /></label>
      <label style={{ display: "grid", minWidth: 0, gap: 4, color: C.light, fontSize: 11 }}>Professor<Select ariaLabel="Professor da aula" value={draft.coachUserId} onChange={value => setDraft({ ...draft, coachUserId: value })} options={members.map(member => ({ value: member.userId, label: `${member.displayName} (${member.roleSlug})` }))} placeholder="Selecione o professor" style={{ width: "100%" }} /></label>
      <label style={{ display: "grid", minWidth: 0, gap: 4, color: C.light, fontSize: 11 }}>Modalidade<Select ariaLabel="Modalidade da aula" value={draft.sportId ?? ""} onChange={value => setDraft({ ...draft, sportId: value || null })} options={sports.map(sport => ({ value: sport.id, label: sport.name }))} disabled={sports.length === 0} placeholder={sports.length === 0 ? "Nenhuma modalidade cadastrada" : "Sem modalidade"} style={{ width: "100%" }} /></label>
      </form><div className="class-create-form__footer"><Button type="submit" form="class-create-form" disabled={loading || !draft.courtId || !draft.coachUserId}>Adicionar</Button></div></Card></div>}
    {pendingDelete && <ConfirmDialog title="Excluir aula" confirmLabel="Excluir aula" onConfirm={() => void confirmRemove()} onCancel={() => setPendingDelete(null)}>Deseja excluir a aula <b>{pendingDelete.name}</b>? Esta ação não pode ser desfeita.</ConfirmDialog>}
    {error && <Snackbar message={error} onClose={() => setError("")} />}
    {success && <Snackbar message={success} kind="success" onClose={() => setSuccess("")} />}
    <div style={{ display: "grid", gap: 8 }}>{classes.map(item => <button key={item.id} type="button" onClick={() => setSelectedClass(item)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 14px", border: `1px solid ${C.faint}`, borderRadius: 10, background: C.white, color: C.ink, font: "inherit", textAlign: "left", cursor: "pointer" }}><span><strong>{item.name}</strong><span style={{ display: "block", color: C.light, fontSize: 11, marginTop: 3 }}>{weekdays[item.dayOfWeek]} · {item.startTime.slice(0, 5)} — {item.endTime.slice(0, 5)} · {item.enrolledCount}/{item.capacity} alunos</span></span><span style={{ color: C.light, fontSize: 11 }}>{item.isActive ? "Ativa" : "Inativa"}</span></button>)}</div>
    {selectedClass && <ClassDetailsDialog token={token} item={selectedClass} onClose={() => setSelectedClass(null)} onChanged={() => { setSelectedClass(null); void load(); onChanged?.(); }} onToggle={async () => { await toggle(selectedClass); setSelectedClass(null); }} onDelete={() => { setPendingDelete(selectedClass); setSelectedClass(null); }} />}
  </section>;
}
